import type {
  AdminExportDataset,
  AdminExportResponse,
  DashboardData,
  DashboardRegistration,
  DailySummaryField,
  DailySummaryPreview,
  EventSettings,
  EventSettingsInput,
  FormState,
  ManualRegistrationInput,
  PublicRegistrationLookup,
  PublicRegistrationConfig,
  RegistrationFilters,
  RegistrationListResponse,
  RegistrationReceipt,
  RegistrationUpdate,
  CheckinDashboard,
} from "./types";

const DEFAULT_PRODUCTION_API_URL = "https://conferencia-lideres-2026-api.amigosdaai.workers.dev";
export const API_URL = (import.meta.env.VITE_API_URL || (import.meta.env.PROD ? DEFAULT_PRODUCTION_API_URL : "")).replace(/\/$/, "");
const ADMIN_TOKEN_KEY = "conference.adminSession";
const ADMIN_OPERATOR_KEY = "conference.adminOperatorName";
const ADMIN_EXPIRES_KEY = "conference.adminSessionExpiresAt";

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message);
    this.name = "ApiError";
  }
}

export async function parseResponse<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string; code?: string } & T;
  if (!response.ok) throw new ApiError(body.error || body.message || "Não foi possível concluir a solicitação.", response.status, body.code);
  return body;
}

function adminHeaders(token = getAdminToken()) {
  return { authorization: `Bearer ${token}` };
}

async function adminResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) clearAdminToken();
  return parseResponse<T>(response);
}

export async function postAnalytics(payload: Record<string, unknown>) {
  const response = await fetch(`${API_URL}/api/analytics`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  });
  if (!response.ok) throw new Error("Falha ao registrar a navegação.");
}

export async function submitRegistration(form: FormState, sessionId: string, durationMs: number) {
  const attendsChurch = form.attendsChurch === "sim";
  const response = await fetch(`${API_URL}/api/registrations`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      ...form,
      attendsChurch,
      attendsCell: attendsChurch ? form.attendsCell === "sim" : null,
      servesMinistry: !attendsChurch ? form.servesMinistry === "sim" : null,
      sessionId,
      durationMs,
    }),
  });
  return parseResponse<RegistrationReceipt>(response);
}

export async function loadPublicConfig() {
  const response = await fetch(`${API_URL}/api/public/config`);
  return parseResponse<PublicRegistrationConfig>(response);
}

export async function loadPublicRegistration(code: string) {
  const response = await fetch(`${API_URL}/api/public/registration?code=${encodeURIComponent(code)}`);
  return parseResponse<PublicRegistrationLookup>(response);
}

export async function adminLogin(operatorName: string, password: string) {
  const normalizedName = operatorName.trim();
  const response = await fetch(`${API_URL}/api/admin/login`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ operatorName: normalizedName, password }),
  });
  const data = await parseResponse<{ token: string; operatorName: string; expiresIn: number }>(response);
  localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
  localStorage.setItem(ADMIN_OPERATOR_KEY, data.operatorName);
  localStorage.setItem(ADMIN_EXPIRES_KEY, String(Date.now() + data.expiresIn * 1000));
  return data;
}

export function getAdminToken() {
  const expiresAt = Number(localStorage.getItem(ADMIN_EXPIRES_KEY) || 0);
  if (expiresAt && expiresAt <= Date.now()) { clearAdminToken(); return ""; }
  return localStorage.getItem(ADMIN_TOKEN_KEY) || "";
}

export function getAdminOperatorName() {
  return localStorage.getItem(ADMIN_OPERATOR_KEY) || "";
}

export function clearAdminToken() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_EXPIRES_KEY);
}

export async function loadDashboard(token = getAdminToken()) {
  const response = await fetch(`${API_URL}/api/admin/dashboard`, {
    headers: adminHeaders(token),
  });
  return adminResponse<DashboardData>(response);
}

export async function loadAdminStats(token = getAdminToken()) {
  const response = await fetch(`${API_URL}/api/admin/stats`, { headers: adminHeaders(token) });
  return adminResponse<import("./types").AdminStats>(response);
}

export async function saveEventSettings(input: EventSettingsInput, expectedUpdatedAt: string) {
  const response = await fetch(`${API_URL}/api/admin/settings`, {
    method: "PATCH",
    headers: { ...adminHeaders(), "content-type": "application/json" },
    body: JSON.stringify({ ...input, expectedUpdatedAt }),
  });
  return adminResponse<{ settings: EventSettings }>(response);
}

export async function loadDailySummaryPreview(referenceDate: string, fields: DailySummaryField[]) {
  const search = new URLSearchParams({ date: referenceDate, fields: fields.join(",") });
  return adminResponse<DailySummaryPreview>(await fetch(`${API_URL}/api/admin/daily-summary?${search}`, { headers: adminHeaders() }));
}

export async function sendDailySummary(referenceDate: string, fields: DailySummaryField[], countsAsDaily: boolean) {
  return adminResponse<{ ok: true; message: string }>(await fetch(`${API_URL}/api/admin/daily-summary`, {
    method: "POST", headers: { ...adminHeaders(), "content-type": "application/json" },
    body: JSON.stringify({ referenceDate, fields, countsAsDaily }),
  }));
}

export async function loadRegistrations(filters: RegistrationFilters, signal?: AbortSignal) {
  const search = new URLSearchParams({
    pageSize: String(filters.pageSize),
    query: filters.query,
    affiliation: filters.affiliation,
    status: filters.status,
    presence: filters.presence,
    collaborator: filters.collaboratorOnly ? "only" : "all",
  });
  if (filters.cursor) search.set("cursor", filters.cursor);
  const response = await fetch(`${API_URL}/api/admin/registrations?${search}`, {
    headers: adminHeaders(),
    signal,
  });
  return adminResponse<RegistrationListResponse>(response);
}

export async function updateRegistration(registration: DashboardRegistration, update: RegistrationUpdate) {
  const response = await fetch(`${API_URL}/api/admin/registrations/${encodeURIComponent(String(registration.id))}`, {
    method: "PATCH",
    headers: { ...adminHeaders(), "content-type": "application/json" },
    body: JSON.stringify({ ...update, expectedUpdatedAt: registration.updatedAt }),
  });
  return adminResponse<{ registration: DashboardRegistration }>(response);
}

export async function setRegistrationActive(registration: DashboardRegistration, active: boolean) {
  const response = await fetch(`${API_URL}/api/admin/registrations/${encodeURIComponent(String(registration.id))}/status`, {
    method: "POST",
    headers: { ...adminHeaders(), "content-type": "application/json" },
    body: JSON.stringify({ active, expectedUpdatedAt: registration.updatedAt }),
  });
  return adminResponse<{ registration: DashboardRegistration }>(response);
}

export async function loadCheckinDashboard(date: string, affiliation: "all" | "church" | "other", cell: "all" | "yes" | "no") {
  const search = new URLSearchParams({ date, affiliation, cell });
  return adminResponse<CheckinDashboard>(await fetch(`${API_URL}/api/admin/checkins?${search}`, { headers: adminHeaders() }));
}

export async function undoAdminCheckin(registration: DashboardRegistration) {
  return adminResponse<{ undoneAt: string }>(await fetch(`${API_URL}/api/admin/registrations/${encodeURIComponent(String(registration.id))}/checkin/undo`, { method: "POST", headers: { ...adminHeaders(), "content-type": "application/json" }, body: JSON.stringify({ confirm: true }) }));
}

export async function createManualRegistration(input: ManualRegistrationInput) {
  const response = await fetch(`${API_URL}/api/admin/registrations`, {
    method: "POST",
    headers: { ...adminHeaders(), "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return adminResponse<{ registration: DashboardRegistration }>(response);
}

export type AdminExportFormat = "csv" | "xlsx" | "json";
type ExportRecord = Record<string, unknown>;

const SENSITIVE_EXPORT_KEY = /(?:password|secret|token|authorization|credential)/i;
const STANDARD_HEADERS = ["Código", "Nome completo", "Telefone", "E-mail", "Vínculo", "Departamento", "Célula", "Ministério", "Função", "Inscrição em", "Atualização em", "Inativação em", "Status"];

function isRecord(value: unknown): value is ExportRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asRecords(value: unknown): ExportRecord[] {
  if (Array.isArray(value)) return value.filter(isRecord);
  return isRecord(value) ? [value] : [];
}

function safeExportValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(safeExportValue);
  if (!isRecord(value)) return value;
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !SENSITIVE_EXPORT_KEY.test(key))
    .map(([key, nested]) => [key, safeExportValue(nested)]));
}

function asText(value: unknown) {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function standardRows(registrations: ExportRecord[]) {
  return registrations.map((registration) => {
    const attendsChurch = registration.attendsChurch === true || registration.attendsChurch === 1 || registration.attendsChurch === "1";
    const affiliation = registration.attendsChurch === null || registration.attendsChurch === undefined
      ? "Não informado"
      : attendsChurch ? "Igreja local" : asText(registration.ministry || "Outra igreja");
    const active = registration.active === true || registration.active === 1 || registration.active === "1";
    return [
      asText(registration.code), asText(registration.fullName), asText(registration.phone), asText(registration.email),
      affiliation,
      asText(registration.department), asText(registration.cellName), asText(registration.ministry), asText(registration.role),
      asText(registration.createdAt), asText(registration.updatedAt), asText(registration.deactivatedAt), active ? "Ativa" : "Inativa",
    ];
  });
}

function recordsToTable(records: ExportRecord[]) {
  const safeRecords = records.map((record) => safeExportValue(record) as ExportRecord);
  const headers = [...new Set(safeRecords.flatMap((record) => Object.keys(record)))];
  return { headers, rows: safeRecords.map((record) => headers.map((header) => asText(record[header]))) };
}

function csvCell(value: unknown) {
  const raw = asText(value);
  const text = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function csvDocument(headers: string[], rows: string[][]) {
  return `\ufeff${[headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n")}\r\n`;
}

function safeFileName(name: string) {
  return name.replace(/[^a-z0-9._-]+/gi, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") || "exportacao";
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function exportDataset(response: AdminExportResponse): AdminExportDataset {
  return response.dataset || response;
}

export async function downloadAdminExport(filters: RegistrationFilters, format: AdminExportFormat, technical: boolean) {
  const search = new URLSearchParams({
    query: filters.query,
    affiliation: filters.affiliation,
    status: filters.status,
    presence: filters.presence,
    collaborator: filters.collaboratorOnly ? "only" : "all",
    format,
    technical: technical ? "1" : "0",
  });
  const response = await fetch(`${API_URL}/api/admin/export?${search}`, { headers: adminHeaders() });
  const dataset = exportDataset(await adminResponse<AdminExportResponse>(response));
  const registrations = asRecords(dataset.registrations);
  const date = new Date().toISOString().slice(0, 10);
  const baseName = safeFileName(`conferencia-lideres-${technical ? "exportacao-tecnica" : "inscricoes"}-${date}`);
  const registrationRows = standardRows(registrations);

  if (format === "csv") {
    if (!technical) {
      downloadBlob(new Blob([csvDocument(STANDARD_HEADERS, registrationRows)], { type: "text/csv;charset=utf-8" }), `${baseName}.csv`);
      return;
    }
    const { createZip } = await import("./exportWorkbook");
    const registrationsTable = recordsToTable(registrations);
    const analyticsTable = recordsToTable(asRecords(dataset.analytics));
    const registrationAuditTable = recordsToTable(asRecords(dataset.registrationAudit));
    const settingsAuditTable = recordsToTable(asRecords(dataset.settingsAudit));
    const zip = createZip([
      { name: "Inscricoes.csv", content: csvDocument(registrationsTable.headers, registrationsTable.rows) },
      { name: "Analytics.csv", content: csvDocument(analyticsTable.headers, analyticsTable.rows) },
      { name: "Auditoria-inscricoes.csv", content: csvDocument(registrationAuditTable.headers, registrationAuditTable.rows) },
      { name: "Auditoria-configuracoes.csv", content: csvDocument(settingsAuditTable.headers, settingsAuditTable.rows) },
    ]);
    downloadBlob(new Blob([zip], { type: "application/zip" }), `${baseName}.zip`);
    return;
  }

  const safeDataset = safeExportValue(technical
    ? dataset
    : { registrations: registrations.map((registration) => ({
      code: registration.code, fullName: registration.fullName, phone: registration.phone, email: registration.email,
      attendsChurch: registration.attendsChurch, department: registration.department, attendsCell: registration.attendsCell,
      cellName: registration.cellName, ministry: registration.ministry, servesMinistry: registration.servesMinistry,
      role: registration.role, active: registration.active, createdAt: registration.createdAt,
      updatedAt: registration.updatedAt, deactivatedAt: registration.deactivatedAt,
    })) });
  if (format === "json") {
    downloadBlob(new Blob([JSON.stringify(safeDataset, null, 2)], { type: "application/json;charset=utf-8" }), `${baseName}.json`);
    return;
  }

  const { createWorkbook } = await import("./exportWorkbook");
  const sheets = technical
    ? [
      { name: "Inscricoes", ...recordsToTable(registrations) },
      { name: "Analytics", ...recordsToTable(asRecords(dataset.analytics)) },
      { name: "Auditoria inscricoes", ...recordsToTable(asRecords(dataset.registrationAudit)) },
      { name: "Auditoria configuracoes", ...recordsToTable(asRecords(dataset.settingsAudit)) },
    ]
    : [{ name: "Inscricoes", headers: STANDARD_HEADERS, rows: registrationRows }];
  downloadBlob(new Blob([createWorkbook(sheets)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${baseName}.xlsx`);
}
